import { DateField } from "@fawkes/ui";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useCustom, useTranslate } from "@refinedev/core";

import { useAnimalPhotoUrls } from "../api/useAnimalPhotoUrls";

import { IndeelbareTabel } from "../components/IndeelbareTabel";
import "./PleeggezinOverzicht.css";

// Pleeggezin-overzicht migration (Track B): the SPA port of the hybrid pleeggezin-overzicht React app
// (frontend-react/pleeggezin-overzicht — Jmix PleeggezinOverzichtView, @Route("pleeggezin-overzicht")).
// The old postMessage bridge (pleeggezin-overzicht:ready/data/historie/actie) is replaced by the api:
//   GET /api/pleeggezinnen/overzicht                  → overzicht 1 (dieren) + 2 (relaties)
//   GET /api/pleeggezinnen/overzicht/historie?van&tot → overzicht 3 (periode-historie)
// and the "details" bridge action becomes plain SPA navigation (onOpenAnimal → animal detail).
// All three tabs are ported faithfully: the dieren/historie card grids share the "Dier aanwezig"
// filter/toolbar vocabulary (search, species/breed/age filters, species stat cards, field chooser,
// grid/compact/list view modes, client-side paging), and the Pleeggezinnen tab renders the relation
// cards with contact data + the capacity & availability section (per-species rows, welkom-category
// occupancy dots, total occupancy bar). Row data (dates, ja/nee, ages, weights) arrives pre-formatted
// from the api byte-for-byte with the Jmix payload — the client only translates its own chrome.

// ── wire types (see api PleeggezinOverzichtModels) ─────────────────────────────────────────────────
export interface DierPlaatsing {
  dierId: number | null;
  fotoUrl: string | null;
  verblijfNummer: string | null;
  dierNaam: string | null;
  ras: string | null;
  diersoort: string | null;
  kleur: string | null;
  vacht: string | null;
  leeftijd: string | null;
  leeftijdJaren: number | null;
  geboortedatum: string | null;
  geslacht: string | null;
  gecastreerd: string | null;
  castratiedatum: string | null;
  paspoortnummer: string | null;
  bijzonderheden: string | null;
  herkomst: string | null;
  opnamedatum: string | null;
  opnamedatumIso: string | null;
  inAsielDagen: number | null;
  gewicht: string | null;
  entingenOk: string | null;
  chipnummer: string | null;
  pleeggezin: string | null;
  woonplaats: string | null;
  vestiging: string | null;
  vanDatum: string | null;
  totDatum: string | null;
  dagen: number;
}

export interface Capaciteit {
  diersoort: string | null;
  max: number;
}

export interface CategorieBezetting {
  naam: string | null;
  max: number;
  bezet: number;
}

export interface DiersoortCapaciteit {
  diersoort: string;
  max: number;
  bezet: number;
  categorieen: CategorieBezetting[];
}

export interface PleeggezinRelatie {
  klantId: number;
  klantNummer: number | null;
  naam: string | null;
  email: string | null;
  telefoon: string | null;
  straat: string | null;
  huisnummer: string | null;
  postcode: string | null;
  woonplaats: string | null;
  actief: boolean;
  vestigingen: string[];
  diersoorten: string[];
  capaciteit: Capaciteit[];
  huidigeBezetting: number;
  totaalMax: number;
  capaciteitDetail: DiersoortCapaciteit[];
}

export interface OverzichtPayload {
  vestigingNaam: string | null;
  rassen: string[];
  dieren: DierPlaatsing[];
  relaties: PleeggezinRelatie[];
}

export interface HistoriePayload {
  van: string | null;
  tot: string | null;
  historie: DierPlaatsing[];
  /** The window held more placements than the server returns; the list stops at that many. */
  afgekapt?: boolean;
  max?: number;
}

const PER_PAGINA = 24;
const PETBASE = "https://www.petbase.eu/chipnummer-zoeken/?chip_number=";

/* ── icons (hybrid inline SVG set — page-local, no shared-component edits) ─────────────────────── */
const PATHS: Record<string, string[]> = {
  paw: ["M11 14c-2 0-4 1.5-4 3.5S9 21 11 21h2c2 0 4-1.5 4-3.5S15 14 13 14z", "M5.5 11a1.6 1.6 0 1 0 0-3.2 1.6 1.6 0 0 0 0 3.2z", "M18.5 11a1.6 1.6 0 1 0 0-3.2 1.6 1.6 0 0 0 0 3.2z", "M9 8a1.6 1.6 0 1 0 0-3.2A1.6 1.6 0 0 0 9 8z", "M15 8a1.6 1.6 0 1 0 0-3.2A1.6 1.6 0 0 0 15 8z"],
  home: ["M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z", "M9 22V12h6v10"],
  mapPin: ["M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z", "M12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"],
  calendar: ["M3 4h18v18H3z", "M16 2v4M8 2v4M3 10h18"],
  clock: ["M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20", "M12 6v6l4 2"],
  search: ["M21 21l-4.35-4.35", "M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z"],
  heart: ["M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 1 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8z"],
  scissors: ["M6 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6z", "M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6z", "M20 4 8.12 15.88", "M14.47 14.48 20 20", "M8.12 8.12 12 12"],
  user: ["M20 21a8 8 0 1 0-16 0", "M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"],
  check: ["M20 6 9 17l-5-5"],
  scale: ["M12 3v18", "M5 8h14", "M5 8l-3 7a4 4 0 0 0 6 0z", "M19 8l-3 7a4 4 0 0 0 6 0z"],
  syringe: ["m18 2 4 4M17 7l3-3M19 9 8.7 19.3a2.4 2.4 0 0 1-3.4 0l-.6-.6a2.4 2.4 0 0 1 0-3.4L15 5M9 11l4 4M5 19l-3 3M14 4l6 6"],
  info: ["M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20", "M12 16v-4", "M12 8h.01"],
  book: ["M4 19.5A2.5 2.5 0 0 1 6.5 17H20", "M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"],
  chip: ["M4 4h16v16H4z", "M9 9h6v6H9z", "M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2"],
  login: ["M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4", "M10 17l5-5-5-5", "M15 12H3"],
  mail: ["M4 4h16v16H4z", "M22 6l-10 7L2 6"],
  phone: ["M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"],
  gear: ["M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z", "M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"],
};

function Icon({ name, size = 16, className }: { name: string; size?: number; className?: string }) {
  const d = PATHS[name] || [];
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {d.map((p, i) => <path key={i} d={p} />)}
    </svg>
  );
}

// Species PNGs shared with the "Dier aanwezig" overview (public/species/*.png).
function soortIcon(naam: string | null | undefined): string {
  const n = (naam || "").toLowerCase();
  if (n.includes("hond")) return "/species/hond.png";
  if (n.includes("kat")) return "/species/kat.png";
  if (n.includes("konijn")) return "/species/konijn.png";
  if (n.includes("vogel")) return "/species/vogel.png";
  if (n.includes("knaag")) return "/species/knaagdier.png";
  return "/species/overig.png";
}

/* Fixed colours grouping animals of the same foster family (hybrid parity). */
const PG_KLEUREN = ["#3b82f6", "#ef4444", "#f59e0b", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#0ea5e9", "#84cc16", "#a855f7", "#e11d48", "#06b6d4"];
export function kleurVoorPleeggezin(naam: string | null | undefined): string {
  if (!naam) return "#9aa6a0";
  let h = 0;
  for (let i = 0; i < naam.length; i++) h = (h * 31 + naam.charCodeAt(i)) >>> 0;
  return PG_KLEUREN[h % PG_KLEUREN.length];
}

const DS_KLEUREN: Record<string, string> = { hond: "#f97316", kat: "#8b5cf6", konijn: "#14b8a6", vogel: "#0ea5e9", knaag: "#a855f7" };
function kleurVoorDiersoort(naam: string | null | undefined): string {
  const n = (naam || "").toLowerCase();
  for (const k of Object.keys(DS_KLEUREN)) if (n.includes(k)) return DS_KLEUREN[k];
  return kleurVoorPleeggezin(naam);
}

/* ── selectable card fields (Velden chooser) ───────────────────────────────────────────────────── */
interface FieldDef {
  key: string;
  labelKey: string;
  plek?: "foto" | "subtitle";
  icon?: string;
  get?: (d: DierPlaatsing) => string | null;
}
const FIELDS: FieldDef[] = [
  { key: "foto", labelKey: "pgOverzicht.veld.foto", plek: "foto" },
  { key: "ras", labelKey: "pgOverzicht.veld.ras", plek: "subtitle" },
  { key: "woonplaats", labelKey: "pgOverzicht.veld.woonplaats", icon: "mapPin", get: (d) => d.woonplaats },
  { key: "leeftijd", labelKey: "pgOverzicht.veld.leeftijd", icon: "calendar", get: (d) => d.leeftijd },
  { key: "diersoort", labelKey: "pgOverzicht.veld.diersoort", icon: "paw", get: (d) => d.diersoort },
  { key: "kleur", labelKey: "pgOverzicht.veld.kleur", icon: "heart", get: (d) => d.kleur },
  { key: "vacht", labelKey: "pgOverzicht.veld.vacht", icon: "scissors", get: (d) => d.vacht },
  { key: "geslacht", labelKey: "pgOverzicht.veld.geslacht", icon: "user", get: (d) => d.geslacht },
  { key: "gecastreerd", labelKey: "pgOverzicht.veld.gecastreerd", icon: "check", get: (d) => d.gecastreerd },
  { key: "geboortedatum", labelKey: "pgOverzicht.veld.geboortedatum", icon: "clock", get: (d) => d.geboortedatum },
  { key: "herkomst", labelKey: "pgOverzicht.veld.herkomst", icon: "mapPin", get: (d) => d.herkomst },
  { key: "opnamedatum", labelKey: "pgOverzicht.veld.opnamedatum", icon: "login", get: (d) => d.opnamedatum },
  { key: "gewicht", labelKey: "pgOverzicht.veld.gewicht", icon: "scale", get: (d) => d.gewicht },
  { key: "entingen", labelKey: "pgOverzicht.veld.entingen", icon: "syringe", get: (d) => d.entingenOk },
  { key: "bijzonderheden", labelKey: "pgOverzicht.veld.bijzonderheden", icon: "info", get: (d) => d.bijzonderheden },
  { key: "castratiedatum", labelKey: "pgOverzicht.veld.castratiedatum", icon: "calendar", get: (d) => d.castratiedatum },
  { key: "paspoortnummer", labelKey: "pgOverzicht.veld.paspoortnummer", icon: "book", get: (d) => d.paspoortnummer },
  { key: "chipnummer", labelKey: "pgOverzicht.veld.chipnummer", icon: "chip", get: (d) => d.chipnummer },
];
const META_FIELDS = FIELDS.filter((f) => f.get && f.plek !== "subtitle" && f.plek !== "foto");
const DEFAULT_KEYS = ["foto", "ras", "woonplaats", "leeftijd", "herkomst", "chipnummer"];
const VELDEN_DEFAULT: Record<string, boolean> = Object.fromEntries(FIELDS.map((f) => [f.key, DEFAULT_KEYS.includes(f.key)]));

/* selectable fields for the Pleeggezinnen (relations) tab */
interface PgFieldDef {
  key: string;
  labelKey: string;
  icon: string;
  get: (r: PleeggezinRelatie, t: (k: string) => string) => string | null;
}
const PG_FIELDS: PgFieldDef[] = [
  { key: "klantnummer", labelKey: "pgOverzicht.pgveld.klantnummer", icon: "chip", get: (r) => (r.klantNummer ? "R" + r.klantNummer : null) },
  { key: "email", labelKey: "pgOverzicht.pgveld.email", icon: "mail", get: (r) => r.email },
  { key: "telefoon", labelKey: "pgOverzicht.pgveld.telefoon", icon: "phone", get: (r) => r.telefoon },
  { key: "plaats", labelKey: "pgOverzicht.pgveld.plaats", icon: "mapPin", get: (r) => r.woonplaats },
  { key: "vestiging", labelKey: "pgOverzicht.pgveld.vestiging", icon: "home", get: (r) => (r.vestigingen || []).join(", ") },
  { key: "diersoorten", labelKey: "pgOverzicht.pgveld.diersoorten", icon: "paw", get: (r) => (r.diersoorten || []).join(", ") },
  { key: "capaciteit", labelKey: "pgOverzicht.pgveld.capaciteit", icon: "scale", get: (r) => (r.capaciteit || []).map((c) => `${c.diersoort}: ${c.max}`).join(", ") },
  { key: "status", labelKey: "pgOverzicht.pgveld.status", icon: "check", get: (r, t) => (r.actief ? t("pgOverzicht.status.actief") : t("pgOverzicht.status.inactief")) },
];
const PG_DEFAULT_KEYS = ["email", "telefoon", "plaats", "vestiging"];
const PG_VELDEN_DEFAULT: Record<string, boolean> = Object.fromEntries(PG_FIELDS.map((f) => [f.key, PG_DEFAULT_KEYS.includes(f.key)]));

/* ── helpers ───────────────────────────────────────────────────────────────────────────────────── */
function toISO(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function defaultVan(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 3);
  return toISO(d);
}
const plainRas = (d: DierPlaatsing) => (d.ras || "").replace(" (kruising)", "");

function ChipWaarde({ chip }: { chip: string | null }) {
  if (!chip) return <>—</>;
  return (
    <a className="chipLink" href={PETBASE + encodeURIComponent(chip)} target="_blank" rel="noreferrer"
       onClick={(e) => e.stopPropagation()}>{chip}</a>
  );
}

/* ── Velden chooser (hybrid dropdown w/ concept state) ─────────────────────────────────────────── */
function VeldenChooser({
  fields,
  velden,
  onChange,
}: {
  fields: { key: string; labelKey: string }[];
  velden: Record<string, boolean>;
  onChange: (v: Record<string, boolean>) => void;
}) {
  const t = useTranslate();
  const [open, setOpen] = useState(false);
  const [concept, setConcept] = useState(velden);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function buiten(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", buiten);
    return () => document.removeEventListener("mousedown", buiten);
  }, []);
  return (
    <div className="veldenChooser" ref={ref}>
      <button type="button" className="veldenBtn"
              onClick={() => (open ? setOpen(false) : (setConcept(velden), setOpen(true)))}>
        <Icon name="gear" className="vCog" /> {t("pgOverzicht.velden")}
      </button>
      {open && (
        <div className="veldenPanel">
          <div className="veldenKop">{t("pgOverzicht.velden.kiezen")}</div>
          <div className="veldenLijst">
            {fields.map((v) => (
              <label key={v.key} className="veldenRij">
                <input type="checkbox" checked={!!concept[v.key]}
                       onChange={() => setConcept({ ...concept, [v.key]: !concept[v.key] })} />
                <span className="veldSwitch" />
                <span className="veldLabel">{t(v.labelKey)}</span>
              </label>
            ))}
          </div>
          <div className="veldenVoet">
            <button type="button" className="vAnnuleer" onClick={() => setOpen(false)}>{t("common.cancel")}</button>
            <button type="button" className="vOpslaan" onClick={() => { onChange(concept); setOpen(false); }}>
              {t("common.save")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── species stat cards (header) ───────────────────────────────────────────────────────────────── */
function SoortFilter({
  soorten,
  totaal,
  actief,
  onKies,
  t,
}: {
  soorten: { naam: string; aantal: number }[];
  totaal: number;
  actief: string;
  onKies: (s: string) => void;
  t: (k: string) => string;
}) {
  if (!soorten.length) return null;
  return (
    <div className="soorten">
      {soorten.map((s) => (
        <button type="button" key={s.naam} className={"soortKaart" + (actief === s.naam ? " actief" : "")}
                onClick={() => onKies(actief === s.naam ? "" : s.naam)}>
          <span className="soortIcon"><img className="soortIconImg" src={soortIcon(s.naam)} alt="" /></span>
          <span className="soortBody"><span className="soortAantal">{s.aantal}</span><span className="soortNaam">{s.naam}</span></span>
        </button>
      ))}
      <button type="button" className={"soortKaart totaal" + (!actief ? " actief" : "")} onClick={() => onKies("")}>
        <span className="soortIcon"><Icon name="paw" size={22} className="soortPaw" /></span>
        <span className="soortBody"><span className="soortAantal">{totaal}</span><span className="soortNaam">{t("pgOverzicht.totaal")}</span></span>
      </button>
    </div>
  );
}

/* ── animal card ───────────────────────────────────────────────────────────────────────────────── */
function DierKaart({
  d,
  fotoUrl,
  velden,
  historie,
  onDetails,
  t,
}: {
  d: DierPlaatsing;
  fotoUrl?: string | null;
  velden: Record<string, boolean>;
  historie?: boolean;
  onDetails: (d: DierPlaatsing) => void;
  t: (k: string) => string;
}) {
  const [geenFoto, setGeenFoto] = useState(false);
  const kleur = kleurVoorPleeggezin(d.pleeggezin);
  const metaRijen = META_FIELDS.filter((f) => velden[f.key]);
  // Op de foto het hele verblijf in het asiel, niet alleen deze plaatsing: dat is de leeftijd van het
  // dossier, en daar kijk je naar als je beoordeelt hoe lang een dier al wacht. Hoe lang het bij dit
  // gezin zit staat onderaan bij de naam van dat gezin.
  const asielDagenTekst = d.inAsielDagen === 1
    ? t("pgOverzicht.asielDag.een")
    : t("pgOverzicht.asielDag.meer").replace("%d", String(d.inAsielDagen));
  // Bij het huisje onderaan: daar staat de naam van het gezin, en dan wil je weten hoe lang het dier
  // er al zit. Het woord "Pleeggezin" herhaalde alleen wat de kaart al zegt.
  const dagenBijTekst = d.dagen === 1
    ? t("pgOverzicht.dagBij.een")
    : t("pgOverzicht.dagBij.meer").replace("%d", String(d.dagen));
  return (
    <div className="kaart" role="button" tabIndex={0} title={t("pgOverzicht.openDier")}
         style={{ borderLeft: `5px solid ${kleur}` }}
         onClick={() => onDetails(d)}
         onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onDetails(d); } }}>
      <div className={"kaartFoto" + (geenFoto || !fotoUrl ? " geenFoto" : "")}>
        {/* Source parity: only render the photo; the `.geenFoto::before` background shows the
            camera-with-paw illustration when there is none (no separate icon element). */}
        {fotoUrl && !geenFoto ? (
          <img src={fotoUrl} alt={d.dierNaam || ""} onError={() => setGeenFoto(true)} />
        ) : null}
        {d.inAsielDagen != null && <span className="fotoStatus groen">{asielDagenTekst}</span>}
      </div>
      <div className="kaartBody">
        <h3>{d.dierNaam || "—"}</h3>
        {velden.ras && <div className="ras">{d.ras || d.diersoort || t("pgOverzicht.onbekendRas")}</div>}
        <div className="metaRijen">
          <div className="mr">
            <Icon name="calendar" size={17} className="mi" />
            {/* Label en waarde in één tekstkolom: als de waarde afbreekt begint de tweede regel onder
                het label en niet onder het icoon. */}
            <span className="mrTekst">
              <b>{historie ? t("pgOverzicht.periode") : t("pgOverzicht.sinds")}</b>{" "}
              <span>{historie ? `${d.vanDatum || "?"} – ${d.totDatum || t("pgOverzicht.nu")}` : (d.vanDatum || "?")}</span>
            </span>
          </div>
          {metaRijen.map((f) => (
            <div className="mr" key={f.key}>
              <Icon name={f.icon!} size={17} className="mi" />
              <span className="mrTekst">
                <b>{t(f.labelKey)}:</b>{" "}
                {f.key === "chipnummer" ? <ChipWaarde chip={d.chipnummer} /> : <span>{f.get!(d) || "—"}</span>}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="kaartFooter">
        <div className="chip">
          <span className="chipLabel">
            <Icon name="home" size={14} className="chipIcon" />{" "}
            {d.dagen != null ? dagenBijTekst : t("pgOverzicht.pleeggezin")}
          </span>
          <span className="chipValue"><span className="pgDot" style={{ background: kleur }} />{d.pleeggezin || "—"}</span>
        </div>
      </div>
    </div>
  );
}

/* ── animal row (list view) ────────────────────────────────────────────────────────────────────── */

/* ── foster relation card (capacity & availability redesign) ───────────────────────────────────── */
function initialen(naam: string | null): string {
  return (naam || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase() || "?";
}

function CapCategorie({ c, kleur, t }: { c: CategorieBezetting; kleur: string; t: (k: string) => string }) {
  const vrij = Math.max((c.max || 0) - (c.bezet || 0), 0);
  return (
    <div className="capCat">
      <span className="capCat__naam">{c.naam}</span>
      {c.max > 0 ? (
        <>
          <span className="capCat__dots">
            {Array.from({ length: c.max }).map((_, i) => (
              <span key={i} className={"capDot" + (i < c.bezet ? " vol" : "")}
                    style={i < c.bezet ? { background: kleur, borderColor: kleur } : undefined} />
            ))}
          </span>
          <span className="capCat__vrij">{t("pgOverzicht.cat.vrij").replace("%d", String(vrij))}</span>
        </>
      ) : (
        <span className="capCat__welkom">{t("pgOverzicht.cat.welkom")}</span>
      )}
    </div>
  );
}

function CapDiersoort({ d, t }: { d: DiersoortCapaciteit; t: (k: string) => string }) {
  const kleur = kleurVoorDiersoort(d.diersoort);
  const vrij = Math.max((d.max || 0) - (d.bezet || 0), 0);
  const plaatsen = d.max === 1 ? t("pgOverzicht.plaats.een") : t("pgOverzicht.plaats.meer").replace("%d", String(d.max));
  return (
    <div className="capDs">
      <div className="capDs__kop">
        <span className="capDs__dot" style={{ background: kleur }} />
        <span className="capDs__naam">{d.diersoort}</span>
        <span className="capDs__plaatsen">{plaatsen}</span>
        <span className={"capDs__vrij" + (vrij === 0 ? " vol" : "")}>{t("pgOverzicht.cat.vrij").replace("%d", String(vrij))}</span>
      </div>
      {(d.categorieen || []).length > 0 && (
        <div className="capCats">
          {d.categorieen.map((c, i) => <CapCategorie key={i} c={c} kleur={kleur} t={t} />)}
        </div>
      )}
    </div>
  );
}

function CapBalk({ bezet, max, t }: { bezet: number; max: number; t: (k: string) => string }) {
  const m = max || 0;
  const pct = m > 0 ? Math.min(100, Math.round((bezet / m) * 100)) : 0;
  const vrij = Math.max(m - bezet, 0);
  const vrijTekst = vrij === 1
    ? t("pgOverzicht.plek.een")
    : t("pgOverzicht.plek.meer").replace("%d", String(vrij));
  return (
    <div className="capBalk">
      <div className="capBalk__spoor"><div className="capBalk__vol" style={{ width: pct + "%" }} /></div>
      <div className="capBalk__legenda">
        <span>{t("pgOverzicht.bezet").replace("%d", String(bezet))}</span>
        <span>{vrijTekst}</span>
      </div>
    </div>
  );
}

function RelatieKaart({ r, velden, t }: { r: PleeggezinRelatie; velden: Record<string, boolean>; t: (k: string) => string }) {
  const kleur = kleurVoorPleeggezin(r.naam);
  const totaalMax = r.totaalMax || 0;
  const totaalVrij = Math.max(totaalMax - (r.huidigeBezetting || 0), 0);
  const adresR1 = [r.straat, r.huisnummer].filter(Boolean).join(" ");
  const adresR2 = [r.postcode, r.woonplaats].filter(Boolean).join("  ");
  const detail = r.capaciteitDetail || [];
  return (
    <div className="relatie" style={{ borderLeft: `5px solid ${kleur}` }}>
      <div className="relatie__hoofd">
        <div className="relatie__avatar" style={{ background: kleur + "22", color: kleur }}>{initialen(r.naam)}</div>
        <div className="relatie__id">
          <span className="relatie__naam">{r.naam || "—"}</span>
          <span className="relatie__rol">{t("pgOverzicht.pleeggezin")}</span>
        </div>
        <div className="relatie__bezetting" title={t("pgOverzicht.nuInHuis.title")}>
          <span className="relatie__bezetting-getal">{r.huidigeBezetting}</span>
          <span className="relatie__bezetting-label">{t("pgOverzicht.nuInHuis")}</span>
        </div>
      </div>

      <div className="relatie__contact">
        {velden.klantnummer && r.klantNummer && (
          <div className="relatie__regel"><Icon name="chip" size={15} className="mi" /><span>R{r.klantNummer}</span></div>
        )}
        {velden.email && r.email && (
          <div className="relatie__regel"><Icon name="mail" size={15} className="mi" /><span>{r.email}</span></div>
        )}
        {velden.telefoon && r.telefoon && (
          <div className="relatie__regel"><Icon name="phone" size={15} className="mi" /><span>{r.telefoon}</span></div>
        )}
        {velden.plaats && (adresR1 || adresR2) && (
          <div className="relatie__regel relatie__regel--adres">
            <Icon name="mapPin" size={15} className="mi" />
            <span className="relatie__adres">
              {adresR1 && <span>{adresR1}</span>}
              {adresR2 && <span>{adresR2}</span>}
            </span>
          </div>
        )}
      </div>

      {velden.vestiging && (r.vestigingen || []).length > 0 && (
        <div className="relatie__bij">
          <span className="relatie__bij-label">{t("pgOverzicht.pleeggezinBij")}</span>
          <div className="chips">{r.vestigingen.map((v, i) => <span className="chip-tag chip-tag--groen" key={i}>{v}</span>)}</div>
        </div>
      )}

      {detail.length > 0 && (
        <div className="cap">
          <div className="cap__kop">
            <span className="cap__titel">{t("pgOverzicht.capaciteitTitel")}</span>
            <span className={"cap__pill" + (totaalVrij === 0 ? " vol" : "")}>
              {t("pgOverzicht.plekkenVrij").replace("%v", String(totaalVrij)).replace("%m", String(totaalMax))}
            </span>
          </div>
          <div className="cap__soorten">
            {detail.map((d) => <CapDiersoort key={d.diersoort} d={d} t={t} />)}
          </div>
          <CapBalk bezet={r.huidigeBezetting || 0} max={totaalMax} t={t} />
        </div>
      )}
    </div>
  );
}

/* ── capacity stat cards (Pleeggezinnen tab header) ────────────────────────────────────────────── */
function CapaciteitStat({
  stats,
  totaal,
  actief,
  onKies,
  t,
}: {
  stats: { naam: string; max: number; vrij: number }[];
  totaal: { max: number; vrij: number };
  actief: string;
  onKies: (s: string) => void;
  t: (k: string) => string;
}) {
  if (!stats.length) return null;
  return (
    <div className="soorten capStat">
      {stats.map((s) => (
        <button type="button" key={s.naam} className={"soortKaart" + (actief === s.naam ? " actief" : "")}
                onClick={() => onKies(actief === s.naam ? "" : s.naam)}>
          <span className="soortIcon"><img className="soortIconImg" src={soortIcon(s.naam)} alt="" /></span>
          <span className="soortBody">
            <span className="soortAantal">{s.vrij}<small> / {s.max}</small></span>
            <span className="soortNaam">{t("pgOverzicht.stat.vrij").replace("%s", s.naam)}</span>
          </span>
        </button>
      ))}
      <button type="button" className={"soortKaart totaal" + (!actief ? " actief" : "")} onClick={() => onKies("")}>
        <span className="soortIcon"><Icon name="heart" size={22} className="soortPaw" /></span>
        <span className="soortBody">
          <span className="soortAantal">{totaal.vrij}<small> / {totaal.max}</small></span>
          <span className="soortNaam">{t("pgOverzicht.stat.totaalVrij")}</span>
        </span>
      </button>
    </div>
  );
}


/* ── shared filter/view panel for the animal cards (tab 1 + Historie) ──────────────────────────── */
function DierenFilterPaneel({
  dieren,
  historie,
  onDetails,
  diersoort,
  setDiersoort,
  voet,
  legeTekst,
}: {
  dieren: DierPlaatsing[];
  historie?: boolean;
  onDetails: (d: DierPlaatsing) => void;
  diersoort: string;
  setDiersoort: (s: string) => void;
  voet?: ReactNode;
  legeTekst?: string;
}) {
  const t = useTranslate();
  const [zoek, setZoek] = useState("");
  const [ras, setRas] = useState("");
  const [leeftijd, setLeeftijd] = useState("");
  const [velden, setVelden] = useState(VELDEN_DEFAULT);
  const [weergave, setWeergave] = useState("grid");
  const [sorteer, setSorteer] = useState("pleeggezin");
  const [pagina, setPagina] = useState(1);

  const soortTellingen = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of dieren) if (d.diersoort) m.set(d.diersoort, (m.get(d.diersoort) || 0) + 1);
    return [...m.entries()].map(([naam, aantal]) => ({ naam, aantal })).sort((a, b) => b.aantal - a.aantal);
  }, [dieren]);
  const rassen = useMemo(() => {
    const s = new Set<string>();
    for (const d of dieren) {
      const r = plainRas(d);
      if (r) s.add(r);
    }
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [dieren]);

  const gefilterd = useMemo(() => {
    const z = zoek.trim().toLowerCase();
    return dieren.filter((d) => {
      if (diersoort && d.diersoort !== diersoort) return false;
      if (ras && plainRas(d) !== ras) return false;
      if (leeftijd) {
        const j = d.leeftijdJaren;
        if (j == null) return false;
        if (leeftijd === "young" && !(j < 1)) return false;
        if (leeftijd === "adult" && !(j >= 1 && j <= 7)) return false;
        if (leeftijd === "senior" && !(j >= 8)) return false;
      }
      if (z) {
        const hooi = `${d.dierNaam || ""} ${d.chipnummer || ""} ${d.verblijfNummer || ""} ${d.ras || ""} ${d.kleur || ""} ${d.pleeggezin || ""} ${d.woonplaats || ""}`.toLowerCase();
        if (!hooi.includes(z)) return false;
      }
      return true;
    });
  }, [dieren, zoek, diersoort, ras, leeftijd]);

  const gesorteerd = useMemo(() => {
    const arr = [...gefilterd];
    arr.sort((a, b) => {
      if (sorteer === "naam") return (a.dierNaam || "").localeCompare(b.dierNaam || "");
      if (sorteer === "nieuwste") return (b.opnamedatumIso || "").localeCompare(a.opnamedatumIso || "");
      const pa = (a.pleeggezin || "").toLowerCase();
      const pb = (b.pleeggezin || "").toLowerCase();
      if (pa !== pb) return pa < pb ? -1 : 1;
      return (a.dierNaam || "").localeCompare(b.dierNaam || "");
    });
    return arr;
  }, [gefilterd, sorteer]);

  const totaalPaginas = Math.max(1, Math.ceil(gesorteerd.length / PER_PAGINA));
  const huidigePagina = Math.min(pagina, totaalPaginas);
  const start = (huidigePagina - 1) * PER_PAGINA;
  const zichtbaar = gesorteerd.slice(start, start + PER_PAGINA);

  // Uploaded photo per visible animal (authenticated blob → object URL); falls back to the feed's
  // online fotoUrl. A plain <img src> can't carry the bearer and the Jmix /dier-foto path 404s here.
  const dierFotoUrls = useAnimalPhotoUrls(zichtbaar.map((d) => d.dierId).filter((id): id is number => id != null));
  const fotoVanDier = (d: DierPlaatsing): string | null =>
    (d.dierId != null ? dierFotoUrls[String(d.dierId)] : undefined) ?? d.fotoUrl;
  useEffect(() => { setPagina(1); }, [zoek, diersoort, ras, leeftijd, sorteer]);

  const wisFilters = () => { setZoek(""); setDiersoort(""); setRas(""); setLeeftijd(""); };
  const lijstKolommen = META_FIELDS.filter((f) => velden[f.key]);
  const leeg = legeTekst ?? t("pgOverzicht.dieren.leeg");

  return (
    <>
      <section className="filters">
        <div className="filterRij">
          <div className="zoek">
            <span className="zoekIcon"><Icon name="search" size={16} /></span>
            <input value={zoek} onChange={(e) => setZoek(e.target.value)}
                   placeholder={t("pgOverzicht.zoek.placeholder")} aria-label={t("pgOverzicht.zoek.placeholder")} />
          </div>
          <label className="veld"><span>{t("pgOverzicht.veld.diersoort")}</span>
            <select value={diersoort} onChange={(e) => setDiersoort(e.target.value)}>
              <option value="">{t("pgOverzicht.alle")}</option>
              {soortTellingen.map((s) => <option key={s.naam} value={s.naam}>{s.naam}</option>)}
            </select>
          </label>
          <label className="veld"><span>{t("pgOverzicht.veld.ras")}</span>
            <select value={ras} onChange={(e) => setRas(e.target.value)}>
              <option value="">{t("pgOverzicht.alle")}</option>
              {rassen.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <label className="veld"><span>{t("pgOverzicht.veld.leeftijd")}</span>
            <select value={leeftijd} onChange={(e) => setLeeftijd(e.target.value)}>
              <option value="">{t("pgOverzicht.alle")}</option>
              <option value="young">{t("pgOverzicht.leeftijd.young")}</option>
              <option value="adult">{t("pgOverzicht.leeftijd.adult")}</option>
              <option value="senior">{t("pgOverzicht.leeftijd.senior")}</option>
            </select>
          </label>
          <button type="button" className="wisBtn" onClick={wisFilters}>
            <span className="wisIcon">↻</span> {t("pgOverzicht.wisFilters")}
          </button>
        </div>
        {voet && <div className="filterVoet">{voet}</div>}
      </section>

      <div className="metaBar">
        <span className="resultaten">
          <span className="paw">🐾</span>
          {gesorteerd.length === 1
            ? t("pgOverzicht.resultaat.een")
            : t("pgOverzicht.resultaat.meer").replace("%d", String(gesorteerd.length))}
        </span>
        <div className="rechts">
          <VeldenChooser fields={FIELDS} velden={velden} onChange={setVelden} />
          <div className="weergaveTabs">
            <button type="button" className={weergave === "grid" ? "actief" : ""} onClick={() => setWeergave("grid")} title={t("pgOverzicht.weergave.kaarten")}>▦</button>
            <button type="button" className={weergave === "compact" ? "actief" : ""} onClick={() => setWeergave("compact")} title={t("pgOverzicht.weergave.compact")}>▥</button>
            <button type="button" className={weergave === "lijst" ? "actief" : ""} onClick={() => setWeergave("lijst")} title={t("pgOverzicht.weergave.lijst")}>☰</button>
          </div>
          <label className="metaSort"><span>{t("pgOverzicht.sorterenOp")}</span>
            <select value={sorteer} onChange={(e) => setSorteer(e.target.value)}>
              <option value="pleeggezin">{t("pgOverzicht.sorteer.pleeggezin")}</option>
              <option value="nieuwste">{t("pgOverzicht.sorteer.nieuwste")}</option>
              <option value="naam">{t("pgOverzicht.sorteer.naam")}</option>
            </select>
          </label>
        </div>
      </div>

      {zichtbaar.length === 0 ? (
        <p className="leeg">{leeg}</p>
      ) : weergave !== "lijst" ? (
        <section className={"grid" + (weergave === "compact" ? " compact" : "")}>
          {zichtbaar.map((d, i) => (
            <DierKaart key={d.dierId ?? i} d={d} fotoUrl={fotoVanDier(d)} velden={velden} historie={historie} onDetails={onDetails} t={t} />
          ))}
        </section>
      ) : (
        <section className="lijst">
          <IndeelbareTabel
            tabelKey="pleeggezin-overzicht-dieren"
            columns={[
              {
                key: "dier", header: t("pgOverzicht.kolom.dier"),
                cell: (d) => (
                  <div className="ldier lstreep"
                       style={{ boxShadow: `inset 4px 0 0 ${kleurVoorPleeggezin(d.pleeggezin)}` }}>
                    {velden.foto && (fotoVanDier(d)
                      ? <img className="lfoto" src={fotoVanDier(d) as string} alt=""
                             onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      : <span className="lfoto lfotoLeeg">🐾</span>)}
                    <div className="ldierTekst">
                      <b>{d.dierNaam || "—"}</b>
                      <span>{d.ras || d.diersoort}</span>
                    </div>
                  </div>
                ),
              },
              {
                key: "pleeggezin", header: t("pgOverzicht.pleeggezin"),
                cell: (d) => (
                  <span className="pgCell">
                    <span className="pgDot" style={{ background: kleurVoorPleeggezin(d.pleeggezin) }} />
                    {d.pleeggezin || "—"}
                  </span>
                ),
              },
              ...lijstKolommen.map((f) => ({
                key: f.key,
                header: t(f.labelKey),
                cell: (d: DierPlaatsing) => (f.key === "chipnummer"
                  ? <ChipWaarde chip={d.chipnummer} />
                  : (f.get!(d) || "—")),
              })),
              {
                key: "acties", header: "", vast: true, align: "right" as const,
                cell: (d) => (
                  <button type="button" className="detailsBtn klein"
                          onClick={(e) => { e.stopPropagation(); onDetails(d); }}>
                    {t("pgOverzicht.details")}
                  </button>
                ),
              },
            ]}
            rows={zichtbaar}
            getRowKey={(d) => String(d.dierId ?? `rij-${zichtbaar.indexOf(d)}`)}
            onRowClick={onDetails}
            empty=""
          />
        </section>
      )}

      {totaalPaginas > 1 && (
        <footer className="voet">
          <span>
            {gesorteerd.length === 0
              ? t("pgOverzicht.pager.geen")
              : t("pgOverzicht.pager.bereik")
                  .replace("%f", String(start + 1))
                  .replace("%t", String(Math.min(start + PER_PAGINA, gesorteerd.length)))
                  .replace("%n", String(gesorteerd.length))}
          </span>
          <div className="pager">
            <button type="button" disabled={huidigePagina <= 1} onClick={() => setPagina(huidigePagina - 1)}>‹</button>
            <span className="dots">{t("pgOverzicht.pager.pagina").replace("%p", String(huidigePagina)).replace("%n", String(totaalPaginas))}</span>
            <button type="button" disabled={huidigePagina >= totaalPaginas} onClick={() => setPagina(huidigePagina + 1)}>›</button>
          </div>
        </footer>
      )}
    </>
  );
}

/* ── root ──────────────────────────────────────────────────────────────────────────────────────── */
type TabKey = "dieren" | "pleeggezinnen" | "historie";

export function PleeggezinOverzicht({ onOpenAnimal }: { onOpenAnimal: (id: string) => void }) {
  const t = useTranslate();
  const [tab, setTab] = useState<TabKey>("dieren");
  const [van, setVan] = useState(defaultVan());
  const [tot, setTot] = useState(toISO(new Date()));
  // The window actually queried (set on "Zoeken" / first tab open); null = not asked yet.
  const [historieQuery, setHistorieQuery] = useState<{ van: string; tot: string } | null>(null);
  const [diersoort, setDiersoort] = useState("");
  const [diersoortH, setDiersoortH] = useState("");
  // Pleeggezinnen-tab filters
  const [pgZoek, setPgZoek] = useState("");
  const [pgDiersoort, setPgDiersoort] = useState("");
  const [pgBeschikbaar, setPgBeschikbaar] = useState(false);
  const [pgInHuis, setPgInHuis] = useState(false);
  const [pgVelden, setPgVelden] = useState(PG_VELDEN_DEFAULT);
  const [pgWeergave, setPgWeergave] = useState("kaart");
  const [pgSorteer, setPgSorteer] = useState("naam");

  const { data: overzichtData } = useCustom({ url: "pleeggezinnen/overzicht", method: "get" });
  const data = overzichtData?.data as OverzichtPayload | undefined;

  const { data: historieData, isFetching: historieBezig } = useCustom({
    url: "pleeggezinnen/overzicht/historie",
    method: "get",
    config: { query: historieQuery ?? {} },
    queryOptions: { enabled: historieQuery != null },
  });
  const historie = historieQuery != null ? (historieData?.data as HistoriePayload | undefined) : undefined;

  const vraagHistorie = () => setHistorieQuery({ van, tot });
  useEffect(() => {
    if (tab === "historie" && historieQuery == null) vraagHistorie();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const dieren = data?.dieren || [];
  const relaties = data?.relaties || [];
  const historieRijen = historie?.historie || [];

  const soortTellingen = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of dieren) if (d.diersoort) m.set(d.diersoort, (m.get(d.diersoort) || 0) + 1);
    return [...m.entries()].map(([naam, aantal]) => ({ naam, aantal })).sort((a, b) => b.aantal - a.aantal);
  }, [dieren]);
  const soortTellingenH = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of historieRijen) if (d.diersoort) m.set(d.diersoort, (m.get(d.diersoort) || 0) + 1);
    return [...m.entries()].map(([naam, aantal]) => ({ naam, aantal })).sort((a, b) => b.aantal - a.aantal);
  }, [historieRijen]);

  const pgDiersoorten = useMemo(() => {
    const s = new Set<string>();
    for (const r of relaties) (r.diersoorten || []).forEach((d) => s.add(d));
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [relaties]);
  const pgCapStats = useMemo(() => {
    const m = new Map<string, { max: number; bezet: number }>();
    for (const r of relaties) for (const d of (r.capaciteitDetail || [])) {
      const e = m.get(d.diersoort) || { max: 0, bezet: 0 };
      e.max += d.max || 0;
      e.bezet += d.bezet || 0;
      m.set(d.diersoort, e);
    }
    return [...m.entries()]
      .map(([naam, v]) => ({ naam, max: v.max, vrij: Math.max(v.max - v.bezet, 0) }))
      .sort((a, b) => b.max - a.max);
  }, [relaties]);
  const pgCapTotaal = useMemo(
    () => pgCapStats.reduce((a, s) => ({ max: a.max + s.max, vrij: a.vrij + s.vrij }), { max: 0, vrij: 0 }),
    [pgCapStats]);
  const relatiesGefilterd = useMemo(() => {
    const q = pgZoek.trim().toLowerCase();
    return relaties.filter((r) => {
      if (pgDiersoort && !(r.diersoorten || []).includes(pgDiersoort)) return false;
      if (pgInHuis && !(r.huidigeBezetting > 0)) return false;
      if (pgBeschikbaar) {
        const max = (r.capaciteit || []).reduce((a, c) => a + (c.max || 0), 0);
        if (!(max > r.huidigeBezetting)) return false;
      }
      if (q) {
        const hooi = `${r.naam || ""} ${r.woonplaats || ""} ${(r.diersoorten || []).join(" ")} ${(r.vestigingen || []).join(" ")}`.toLowerCase();
        if (!hooi.includes(q)) return false;
      }
      return true;
    });
  }, [relaties, pgZoek, pgDiersoort, pgBeschikbaar, pgInHuis]);
  const relatiesGesorteerd = useMemo(() => {
    const vrij = (r: PleeggezinRelatie) =>
      (r.capaciteit || []).reduce((a, c) => a + (c.max || 0), 0) - r.huidigeBezetting;
    const arr = [...relatiesGefilterd];
    arr.sort((a, b) => {
      if (pgSorteer === "plaats") return (a.woonplaats || "").localeCompare(b.woonplaats || "");
      if (pgSorteer === "inhuis") return b.huidigeBezetting - a.huidigeBezetting;
      if (pgSorteer === "vrij") return vrij(b) - vrij(a);
      return (a.naam || "").localeCompare(b.naam || "");
    });
    return arr;
  }, [relatiesGefilterd, pgSorteer]);
  const pgWis = () => { setPgZoek(""); setPgDiersoort(""); setPgBeschikbaar(false); setPgInHuis(false); };

  const onDetails = (d: DierPlaatsing) => d.dierId && onOpenAnimal(String(d.dierId));

  const tabs: { key: TabKey; label: string; aantal: number | null }[] = [
    { key: "dieren", label: t("pgOverzicht.tab.dieren"), aantal: dieren.length },
    { key: "pleeggezinnen", label: t("pgOverzicht.tab.pleeggezinnen"), aantal: relaties.length },
    { key: "historie", label: t("pgOverzicht.tab.historie"), aantal: null },
  ];

  return (
    <div className="fawkes-pgo">
      <header className="pg__head">
        <div className="pg__titel">
          <div className="pg__avatar"><Icon name="heart" size={22} /></div>
          <div>
            <span className="pg__overline">{t("pgOverzicht.pleeggezin")}</span>
            <h1>{t("pgOverzicht.title")}</h1>
            {data?.vestigingNaam && <span className="pg__vestiging">{data.vestigingNaam}</span>}
          </div>
        </div>
        {tab === "dieren" && (
          <SoortFilter soorten={soortTellingen} totaal={dieren.length} actief={diersoort} onKies={setDiersoort} t={t} />
        )}
        {tab === "historie" && historie && (
          <SoortFilter soorten={soortTellingenH} totaal={historieRijen.length} actief={diersoortH} onKies={setDiersoortH} t={t} />
        )}
        {tab === "pleeggezinnen" && pgCapStats.length > 0 && (
          <CapaciteitStat stats={pgCapStats} totaal={pgCapTotaal} actief={pgDiersoort} onKies={setPgDiersoort} t={t} />
        )}
      </header>

      <nav className="tabbar">
        {tabs.map(({ key, label, aantal }) => (
          <button key={key} type="button" className={"tabbar__tab" + (key === tab ? " is-active" : "")}
                  onClick={() => setTab(key)}>
            {label}{aantal != null && data ? <span className="tabbar__badge">{aantal}</span> : null}
          </button>
        ))}
      </nav>

      <div className="pg__body">
        {/* Tab 1 */}
        {tab === "dieren" && (
          !data ? <p className="leeg">{t("common.loading")}</p> :
          <DierenFilterPaneel dieren={dieren} onDetails={onDetails} diersoort={diersoort} setDiersoort={setDiersoort} />
        )}

        {/* Tab 2 */}
        {tab === "pleeggezinnen" && (
          <>
            {data && relaties.length > 0 && (
              <section className="filters">
                <div className="pgFilterRij">
                  <div className="zoek"><span className="zoekIcon"><Icon name="search" size={16} /></span>
                    <input value={pgZoek} onChange={(e) => setPgZoek(e.target.value)}
                           placeholder={t("pgOverzicht.pgZoek.placeholder")} aria-label={t("pgOverzicht.pgZoek.placeholder")} /></div>
                  <label className="veld"><span>{t("pgOverzicht.veld.diersoort")}</span>
                    <select value={pgDiersoort} onChange={(e) => setPgDiersoort(e.target.value)}>
                      <option value="">{t("pgOverzicht.alle")}</option>
                      {pgDiersoorten.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </label>
                  <div className="pgToggles">
                    <button type="button" className={"toggleChip" + (pgBeschikbaar ? " aan" : "")}
                            onClick={() => setPgBeschikbaar((v) => !v)}>
                      <span className="toggleDot" /> {t("pgOverzicht.filter.beschikbaar")}
                    </button>
                    <button type="button" className={"toggleChip" + (pgInHuis ? " aan" : "")}
                            onClick={() => setPgInHuis((v) => !v)}>
                      <span className="toggleDot" /> {t("pgOverzicht.filter.metDieren")}
                    </button>
                  </div>
                  <button type="button" className="wisBtn" onClick={pgWis}>
                    <span className="wisIcon">↻</span> {t("pgOverzicht.wisFilters")}
                  </button>
                </div>
              </section>
            )}
            {data && relaties.length > 0 && (
              <div className="metaBar">
                <span className="resultaten">
                  <span className="paw">🐾</span>
                  {relatiesGesorteerd.length === 1
                    ? t("pgOverzicht.pgResultaat.een")
                    : t("pgOverzicht.pgResultaat.meer").replace("%d", String(relatiesGesorteerd.length))}
                </span>
                <div className="rechts">
                  <VeldenChooser fields={PG_FIELDS} velden={pgVelden} onChange={setPgVelden} />
                  <div className="weergaveTabs">
                    <button type="button" className={pgWeergave === "kaart" ? "actief" : ""} onClick={() => setPgWeergave("kaart")} title={t("pgOverzicht.weergave.kaarten")}>▦</button>
                    <button type="button" className={pgWeergave === "lijst" ? "actief" : ""} onClick={() => setPgWeergave("lijst")} title={t("pgOverzicht.weergave.lijst")}>☰</button>
                  </div>
                  <label className="metaSort"><span>{t("pgOverzicht.sorterenOp")}</span>
                    <select value={pgSorteer} onChange={(e) => setPgSorteer(e.target.value)}>
                      <option value="naam">{t("pgOverzicht.sorteer.naam")}</option>
                      <option value="plaats">{t("pgOverzicht.sorteer.plaats")}</option>
                      <option value="inhuis">{t("pgOverzicht.sorteer.inhuis")}</option>
                      <option value="vrij">{t("pgOverzicht.sorteer.vrij")}</option>
                    </select>
                  </label>
                </div>
              </div>
            )}
            {!data ? (<p className="leeg">{t("common.loading")}</p>) : relaties.length === 0 ? (
              <p className="leeg">{t("pgOverzicht.relaties.geen")}</p>
            ) : relatiesGesorteerd.length === 0 ? (
              <p className="leeg">{t("pgOverzicht.relaties.leegFilter")}</p>
            ) : pgWeergave === "lijst" ? (
              <section className="lijst">
                <IndeelbareTabel
                  tabelKey="pleeggezin-overzicht-gezinnen"
                  columns={[
                    {
                      key: "pleeggezin", header: t("pgOverzicht.pleeggezin"),
                      cell: (r) => (
                        // De kleurstreep hing aan de cel; nu aan de inhoud, die zich met .lstreep over
                        // de celrand uitstrekt zodat de streep nog even hoog is als de rij.
                        <div className="ldierTekst lstreep"
                             style={{ boxShadow: `inset 4px 0 0 ${kleurVoorPleeggezin(r.naam)}` }}>
                          <b>{r.naam || "—"}</b><span>{t("pgOverzicht.pleeggezin")}</span>
                        </div>
                      ),
                    },
                    { key: "nuInHuis", header: t("pgOverzicht.kolom.nuInHuis"), cell: (r) => r.huidigeBezetting },
                    // Welke velden erbij staan kiest de gebruiker met "Velden"; dat bleef zo.
                    ...PG_FIELDS.filter((f) => pgVelden[f.key]).map((f) => ({
                      key: f.key,
                      header: t(f.labelKey),
                      cell: (r: PleeggezinRelatie) => f.get(r, t) || "—",
                    })),
                  ]}
                  rows={relatiesGesorteerd}
                  getRowKey={(r) => String(r.klantId)}
                  empty=""
                />
              </section>
            ) : (
              <div className="relaties">
                {relatiesGesorteerd.map((r) => <RelatieKaart key={r.klantId} r={r} velden={pgVelden} t={t} />)}
              </div>
            )}
          </>
        )}

        {/* Tab 3 */}
        {tab === "historie" && (
          <DierenFilterPaneel
            dieren={historieRijen}
            historie
            onDetails={onDetails}
            diersoort={diersoortH}
            setDiersoort={setDiersoortH}
            legeTekst={historieBezig ? t("pgOverzicht.historie.zoeken") : !historie ? t("pgOverzicht.historie.kies") : t("pgOverzicht.historie.leeg")}
            voet={
              <div className="periode">
                <div className="periode__veld">
                  <label htmlFor="pgo-van">{t("pgOverzicht.historie.van")}</label>
                  <DateField id="pgo-van" value={van} onChange={(e) => setVan(e.target.value)} />
                </div>
                <div className="periode__veld">
                  <label htmlFor="pgo-tot">{t("pgOverzicht.historie.tot")}</label>
                  <DateField id="pgo-tot" value={tot} onChange={(e) => setTot(e.target.value)} />
                </div>
                <button className="btn btn--primary" type="button" onClick={vraagHistorie} disabled={historieBezig}>
                  <Icon name="search" size={15} /> {historieBezig ? t("pgOverzicht.historie.zoeken") : t("pgOverzicht.historie.zoek")}
                </button>
                {/* Say it when the answer is cut: a capped list otherwise reads as "this is all". */}
                {historie?.afgekapt && (
                  <span className="periode__kap">
                    {t("pgOverzicht.historie.afgekapt").replace("%d", String(historie.max ?? 0))}
                  </span>
                )}
              </div>
            }
          />
        )}
      </div>
    </div>
  );
}
